<script setup>
// Estúdio Social — aba PRODUÇÃO (pedido do social media da conta 1, 26/08):
// pipeline interno do conteúdo (briefing → gravação → edição → aprovação →
// finalizado), com contagem por etapa e os posts de cada coluna. Clique no
// card abre o mesmo PostEditor do calendário.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import socialAPI from 'dashboard/api/social';
import PostEditor from './PostEditor.vue';
import SocialNav from './SocialNav.vue';

const { t } = useI18n();

const STATUSES = ['briefing', 'gravacao', 'edicao', 'aprovacao', 'finalizado'];
const STATUS_TONES = {
  briefing: 'bg-n-slate-7',
  gravacao: 'bg-n-amber-9',
  edicao: 'bg-n-iris-9',
  aprovacao: 'bg-n-blue-9',
  finalizado: 'bg-n-teal-9',
};

const isLoading = ref(true);
const posts = ref([]);
const editorRef = ref(null);

const fetchPosts = async () => {
  isLoading.value = true;
  try {
    const { data } = await socialAPI.productionPosts();
    posts.value = data || [];
  } catch {
    posts.value = [];
  } finally {
    isLoading.value = false;
  }
};

const byStatus = computed(() => {
  const grupos = Object.fromEntries(STATUSES.map(st => [st, []]));
  posts.value.forEach(post => {
    if (grupos[post.production_status])
      grupos[post.production_status].push(post);
  });
  return grupos;
});

// Post sem etapa (todos os gerados pela IA, por exemplo) ficava fora do
// quadro, só contado. Agora tem coluna própria, primeira à esquerda.
const semStatus = computed(() =>
  posts.value.filter(p => !STATUSES.includes(p.production_status))
);
const columns = computed(() => [
  ...(semStatus.value.length ? [{ key: 'none', posts: semStatus.value }] : []),
  ...STATUSES.map(st => ({ key: st, posts: byStatus.value[st] })),
]);
const columnLabel = key =>
  key === 'none'
    ? t('SOCIAL_STUDIO.PRODUCTION.NO_STATUS_COLUMN')
    : t(`SOCIAL_STUDIO.PRODUCTION.STATUSES.${key}`);
const mediaIcon = post => {
  if (post.media_content_type?.startsWith('video/'))
    return 'i-lucide-clapperboard';
  if ((post.medias_urls?.length || 0) > 1)
    return 'i-lucide-gallery-horizontal-end';
  return post.media_url ? 'i-lucide-image' : null;
};

const cardLabel = post =>
  post.title ||
  (post.content || '').split(/\s+/).slice(0, 6).join(' ') ||
  t('SOCIAL_STUDIO.PRODUCTION.UNTITLED');

const formatDate = value =>
  value
    ? new Date(value).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
      })
    : '';

const openPost = id => editorRef.value?.open(id);
const onSaved = () => fetchPosts();

onMounted(fetchPosts);
</script>

<template>
  <div class="flex flex-col flex-1 overflow-auto bg-n-background">
    <div class="px-6 py-5 max-w-6xl w-full mx-auto space-y-5">
      <div class="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ t('SOCIAL_STUDIO.PRODUCTION.TITLE') }}
          </h1>
          <p class="text-sm text-n-slate-10">
            {{ t('SOCIAL_STUDIO.PRODUCTION.SUBTITLE') }}
          </p>
        </div>
        <SocialNav />
      </div>

      <div v-if="isLoading" class="flex justify-center py-12">
        <span
          class="i-lucide-loader-circle w-6 h-6 animate-spin text-n-slate-9"
        />
      </div>

      <template v-else>
        <!-- Contadores -->
        <div class="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div
            v-for="st in STATUSES"
            :key="`tile-${st}`"
            class="p-4 rounded-xl bg-n-solid-2 border border-n-weak"
          >
            <div class="flex items-center gap-2">
              <span
                class="w-2.5 h-2.5 rounded-full"
                :class="STATUS_TONES[st]"
              />
              <p class="text-xs text-n-slate-10">
                {{ t(`SOCIAL_STUDIO.PRODUCTION.STATUSES.${st}`) }}
              </p>
            </div>
            <p class="mt-1 text-xl font-semibold text-n-slate-12 tabular-nums">
              {{ byStatus[st].length }}
            </p>
          </div>
        </div>
        <!-- Colunas -->
        <div
          class="grid gap-4 md:grid-cols-3"
          :class="columns.length > 5 ? 'xl:grid-cols-6' : 'xl:grid-cols-5'"
        >
          <div
            v-for="column in columns"
            :key="column.key"
            :data-test="`column-${column.key}`"
            class="space-y-2 min-w-0"
          >
            <p
              class="text-xs font-medium uppercase tracking-wide text-n-slate-10 flex items-center gap-1.5"
            >
              <span
                class="w-2 h-2 rounded-full"
                :class="STATUS_TONES[column.key] || 'border border-n-slate-8'"
              />
              {{ columnLabel(column.key) }}
            </p>
            <button
              v-for="post in column.posts"
              :key="post.id"
              class="w-full text-left p-3 rounded-xl bg-n-solid-2 border border-n-weak hover:border-n-brand/50 transition-colors space-y-1.5"
              @click="openPost(post.id)"
            >
              <p class="text-sm text-n-slate-12 line-clamp-2">
                {{ cardLabel(post) }}
              </p>
              <div class="flex items-center gap-2 text-[11px] text-n-slate-10">
                <span v-if="post.scheduled_at" class="flex items-center gap-1">
                  <span class="i-lucide-calendar w-3 h-3" />
                  {{ formatDate(post.scheduled_at) }}
                </span>
                <a
                  v-if="post.drive_link"
                  :href="post.drive_link"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center gap-1 text-n-brand hover:underline"
                  @click.stop
                >
                  <span class="i-lucide-hard-drive w-3 h-3" />
                  {{ t('SOCIAL_STUDIO.PRODUCTION.DRIVE') }}
                </a>
                <span
                  v-if="mediaIcon(post)"
                  class="w-3 h-3"
                  :class="mediaIcon(post)"
                  :title="t('SOCIAL_STUDIO.PRODUCTION.HAS_MEDIA')"
                />
              </div>
            </button>
            <p
              v-if="!column.posts.length"
              class="text-xs text-n-slate-9 italic px-1"
            >
              {{ t('SOCIAL_STUDIO.PRODUCTION.EMPTY_COLUMN') }}
            </p>
          </div>
        </div>
      </template>

      <PostEditor ref="editorRef" @saved="onSaved" />
    </div>
  </div>
</template>
